
import { Component } from 'vue-property-decorator';
import Page from '@/Page.vue';
import GanttToolbar from '@/libs/tpm-analytics/components/GanttToolbar.vue';
import GanttChart from '@/libs/tpm-analytics/components/GanttChart.vue';
import { GanttTimelineService } from '@/libs/tpm-analytics/services/gantt-timeline.service';
import {
    GanttTimelineData,
    GanttTimelineFilters,
    GanttGranularity,
} from '@/libs/tpm-analytics/core/gantt-timeline.types';
import moment from 'moment';

@Component({
    components: {
        GanttToolbar,
        GanttChart,
    },
})
export default class MachineTimeline extends Page {
    // State
    loading = false;
    error: string | null = null;
    ganttData: GanttTimelineData | null = null;

    // Filter state
    periodPreset = 'last7d';
    customFrom: string | null = null;
    customTo: string | null = null;
    selectedLocationIds: string[] = [];
    selectedWorkplaceIds: string[] = [];
    selectedProjectIds: string[] = [];
    selectedShiftTypeIds: string[] = [];
    selectedShiftTeamTypeIds: string[] = [];

    // Gantt configuration
    granularity: GanttGranularity = 'hourly';
    zoom = 1.0;

    // Date pickers state
    showFromPicker = false;
    showToPicker = false;

    // Snackbar
    snackbar = {
        visible: false,
        text: '',
        color: 'success',
    };

    created() {
        // Načíst data
        this.loadData();
    }

    get periodPresets() {
        return [
            { text: this.$t('common.today'), value: 'today' },
            { text: this.$t('common.yesterday'), value: 'previousDay' },
            { text: this.$t('common.last7days'), value: 'last7d' },
            { text: this.$t('common.last14days'), value: 'last14d' },
            { text: this.$t('common.last30days'), value: 'last30d' },
            { text: this.$t('common.thisWeek'), value: 'thisWeek' },
            { text: this.$t('common.lastWeek'), value: 'lastWeek' },
            { text: this.$t('common.thisMonth'), value: 'thisMonth' },
            { text: this.$t('common.lastMonth'), value: 'lastMonth' },
            { text: this.$t('common.custom'), value: 'custom' },
        ];
    }

    /**
     * Pixels per hour s aplikovaným zoomem
     */
    get pixelsPerHour(): number {
        return 30 * this.zoom;
    }

    /**
     * From date ISO
     */
    get fromDateIso(): string {
        if (this.customFrom) return this.customFrom;
        return this.getDateRangeFromPreset().from;
    }

    set fromDateIso(value: string) {
        this.customFrom = value;
        this.periodPreset = 'custom';
        this.loadData();
    }

    /**
     * To date ISO
     */
    get toDateIso(): string {
        if (this.customTo) return this.customTo;
        return this.getDateRangeFromPreset().to;
    }

    set toDateIso(value: string) {
        this.customTo = value;
        this.periodPreset = 'custom';
        this.loadData();
    }

    /**
     * Získá aktuální stav filtrů
     */
    getCurrentFilterState(): any {
        return {
            periodPreset: this.periodPreset,
            customFrom: this.customFrom,
            customTo: this.customTo,
            selectedLocationIds: this.selectedLocationIds,
            selectedWorkplaceIds: this.selectedWorkplaceIds,
            selectedProjectIds: this.selectedProjectIds,
            selectedShiftTypeIds: this.selectedShiftTypeIds,
            selectedShiftTeamTypeIds: this.selectedShiftTeamTypeIds,
            granularity: this.granularity,
        };
    }

    /**
     * Aplikuje stav filtrů
     */
    applyFilterState(state: any) {
        if (!state) return;

        if (state.periodPreset) this.periodPreset = state.periodPreset;
        if (state.customFrom !== undefined) this.customFrom = state.customFrom;
        if (state.customTo !== undefined) this.customTo = state.customTo;
        if (Array.isArray(state.selectedLocationIds)) this.selectedLocationIds = state.selectedLocationIds;
        if (Array.isArray(state.selectedWorkplaceIds)) this.selectedWorkplaceIds = state.selectedWorkplaceIds;
        if (Array.isArray(state.selectedProjectIds)) this.selectedProjectIds = state.selectedProjectIds;
        if (Array.isArray(state.selectedShiftTypeIds)) this.selectedShiftTypeIds = state.selectedShiftTypeIds;
        if (Array.isArray(state.selectedShiftTeamTypeIds)) this.selectedShiftTeamTypeIds = state.selectedShiftTeamTypeIds;
        if (state.granularity) this.granularity = state.granularity;

        this.loadData();
    }


    /**
     * Získá datum rozsah z preset hodnoty
     */
    getDateRangeFromPreset(): { from: string; to: string } {
        let from: moment.Moment;
        let to: moment.Moment;

        switch (this.periodPreset) {
            case 'previousDay':
                from = moment().subtract(1, 'day').startOf('day');
                to = moment().subtract(1, 'day').endOf('day');
                break;
            case 'last7d':
                from = moment().subtract(7, 'days').startOf('day');
                to = moment().endOf('day');
                break;
            case 'last14d':
                from = moment().subtract(14, 'days').startOf('day');
                to = moment().endOf('day');
                break;
            case 'last30d':
                from = moment().subtract(30, 'days').startOf('day');
                to = moment().endOf('day');
                break;
            case 'thisWeek':
                from = moment().startOf('isoWeek');
                to = moment().endOf('isoWeek');
                break;
            case 'lastWeek':
                from = moment().subtract(1, 'week').startOf('isoWeek');
                to = moment().subtract(1, 'week').endOf('isoWeek');
                break;
            case 'thisMonth':
                from = moment().startOf('month');
                to = moment().endOf('month');
                break;
            case 'lastMonth':
                from = moment().subtract(1, 'month').startOf('month');
                to = moment().subtract(1, 'month').endOf('month');
                break;
            default:
                from = moment().subtract(7, 'days').startOf('day');
                to = moment().endOf('day');
        }

        return {
            from: from.toISOString(),
            to: to.toISOString(),
        };
    }

    /**
     * Načte data z API
     */
    async loadData() {
        this.loading = true;
        this.error = null;

        try {
            const filters: GanttTimelineFilters = {
                from: this.fromDateIso,
                to: this.toDateIso,
                locationIds: this.selectedLocationIds.length > 0 ? this.selectedLocationIds : undefined,
                workplaceIds: this.selectedWorkplaceIds.length > 0 ? this.selectedWorkplaceIds : undefined,
                projectIds: this.selectedProjectIds.length > 0 ? this.selectedProjectIds : undefined,
                shiftTypeIds: this.selectedShiftTypeIds.length > 0 ? this.selectedShiftTypeIds : undefined,
                shiftTeamTypeIds: this.selectedShiftTeamTypeIds.length > 0 ? this.selectedShiftTeamTypeIds : undefined,
            };

            this.ganttData = await GanttTimelineService.getGanttTimelineData(
                this.fromDateIso,
                this.toDateIso,
                filters
            );
            
            // Debug výpis
            console.log('=== GANTT DATA DEBUG ===');
            console.log('Počet pracovišť:', this.ganttData?.workplaces?.length || 0);
            console.log('Časový rozsah:', this.fromDateIso, 'až', this.toDateIso);
            console.log('Filtry:', filters);
            if (this.ganttData?.workplaces) {
                this.ganttData.workplaces.forEach((wp, idx) => {
                    console.log(`Pracoviště ${idx + 1}: ${wp.workplaceName}`);
                    console.log(`  - Segmenty celkem: ${wp.segments.length}`);
                    console.log(`  - Běh: ${wp.segments.filter(s => s.type === 'running').length}`);
                    console.log(`  - Plánované odstávky: ${wp.segments.filter(s => s.type === 'planned-downtime').length}`);
                    console.log(`  - Neplánované odstávky: ${wp.segments.filter(s => s.type === 'unplanned-downtime').length}`);
                    console.log(`  - Události: ${wp.events.length}`);
                    
                    // Detail segmentů
                    wp.segments.forEach((seg, sidx) => {
                        const duration = seg.to ? (seg.to.getTime() - seg.from.getTime()) / (1000 * 60 * 60) : 'ongoing';
                        console.log(`    Segment ${sidx + 1}: ${seg.type} | ${seg.from.toISOString()} - ${seg.to?.toISOString() || 'ongoing'} | ${duration}h`);
                    });
                });
            }
            console.log('========================');
        } catch (err: any) {
            this.error = err?.message || this.$t('common.errorLoadingData').toString();
            console.error('Error loading gantt timeline data:', err);
        } finally {
            this.loading = false;
        }
    }

    /**
     * Handler pro změnu zoomu
     */
    onZoomChanged(newZoom: number) {
        this.zoom = newZoom;
    }

    /**
     * Handler pro scroll na datum
     */
    onScrollToDate(date: Date) {
        // TODO: Implementovat scroll v GanttChart komponentě
        console.log('Scroll to date:', date);
    }

    /**
     * Toast notifikace
     */
    toast(text: string, color = 'success') {
        this.snackbar.text = text;
        this.snackbar.color = color;
        this.snackbar.visible = true;
    }
}
